<template>
  <box-label-border :label="label" class="h-full">
    <template v-slot:tools>
      <button @click="copyFleet" :title="$t('components.fleet.copy_tooltip')">
        <icon-copy-list />
      </button>
      <button
        v-if="
          !fleet.lockSpecialists ||
            !fleet.lockResearch ||
            !fleet.lockShips ||
            !fleet.lockDefenses
        "
        @click="clearFleet"
        :title="$t('components.fleet.clear_tooltip')"
      >
        <icon-erase />
      </button>
    </template>
    <template>
      <div class="subfleets-container h-full">
        <sub-fleet
          :key="index - 1"
          :index="index - 1"
          :fleet="fleets[index - 1]"
          v-for="index in fleetCount"
          :style="{ 'margin-bottom': '1rem' }"
          :role="role"
          :drag-bus="dragBus"
          :simulation-bus="simulationBus"
          :item-type-selection-bus="itemTypeSelectionBus"
          :special-ship-bus="specialShipBus"
          :allow-remove="index - 1 > 0"
          @remove="removeSubfleet(index - 1)"
          @import="onFleetImport"
        ></sub-fleet>
        <button-action-small
          v-if="!fleet.lockShips"
          :label="$t('components.fleet.add_subfleet')"
          @click="addSubfleet"
          ><template v-slot:icon><icon-add></icon-add></template
        ></button-action-small>
      </div>
      <fleet-item-selection :selection-bus="itemTypeSelectionBus" />
      <special-ship-settings :special-ship-bus="specialShipBus" />
    </template>
    <template v-slot:overlay>
      <div
        class="absolute copy-success-overlay top-0 left-0 w-full h-full"
        :class="{
          'copy-success-overlay-visible': showCopySuccessOverlay
        }"
      >
        <span
          class="overlay-text"
          v-html="$t('components.fleet.copy_success')"
        ></span>
      </div>
    </template>
  </box-label-border>
</template>

<script>
import { mapActions } from "vuex";

import BoxLabelBorder from "@/components/boxes/BoxLabelBorder";
import SubFleet from "@/components/fleet/SubFleet";
import ButtonActionSmall from "@/components/buttons/ButtonActionSmall";
import IconAdd from "@/components/icons/icon-add";
import Fleet from "@/store/model/fleet";
import Vue from "vue";
import FleetItemSelection from "@/components/fleet/FleetItemPalette";
import SpecialShipSettings from "@/components/Ships/SpecialShipSettings";
import IconErase from "@/components/icons/icon-erase";
import IconCopyList from "@/components/icons/icon-copyList";

export default {
  name: "FleetEditor",
  components: {
    IconCopyList,
    IconErase,
    SpecialShipSettings,
    FleetItemSelection,
    IconAdd,
    ButtonActionSmall,
    SubFleet,
    BoxLabelBorder
  },
  props: {
    label: {
      type: String,
      default: "Fleet"
    },
    defaultName: {
      type: String,
      default: "Fleet"
    },
    fleet: {
      type: Fleet,
      required: true
    },
    role: {
      type: String,
      required: true
    },
    dragBus: {
      type: Vue,
      required: true
    },
    simulationBus: {
      type: Vue,
      default: null
    }
  },
  data: () => {
    return {
      fleets: [],
      itemTypeSelectionBus: new Vue(),
      specialShipBus: new Vue(),
      showCopySuccessOverlay: false
    };
  },
  computed: {
    fleetCount() {
      return this.fleets.length;
    }
  },
  created() {
    if (!this.fleet) {
      console.error("No fleet assigned.");
      return;
    }
    this.onFleetChanged();
  },
  watch: {
    fleet() {
      this.onFleetChanged();
    }
  },
  methods: {
    ...mapActions("combat", ["markFleetsChanged"]),
    onFleetChanged() {
      const fleet = this.fleet;
      let subfleets = fleet.subfleets;
      this.fleets.splice(0);
      this.fleets.push(fleet);
      subfleets.forEach(f => this.fleets.push(f));
    },
    addSubfleet() {
      const fleet = new Fleet();
      this.fleet.subfleets.push(fleet);
      this.fleets.push(fleet);
      this.markFleetsChanged();
    },
    removeSubfleet(index) {
      this.fleet.subfleets.splice(index - 1, 1);
      this.fleets.splice(index, 1);
      this.markFleetsChanged();
    },
    copyFleet() {
      const fleet = this.fleet;
      const data = fleet.serialize();
      navigator.clipboard.writeText(data);
      this.showCopySuccessOverlay = true;
      setTimeout(() => (this.showCopySuccessOverlay = false), 1000);
    },
    clearFleet() {
      if (!confirm(this.$t("components.fleet.clear_prompt"))) {
        return;
      }

      const fleet = this.fleet;
      fleet.reset();
      this.fleets.splice(1, this.fleets.length - 1);
      this.markFleetsChanged();
    },
    onFleetImport(event) {
      if (event.subfleets == null) {
        return;
      }
      this.fleets.splice(1);
      this.fleet.subfleets.forEach(f => this.fleets.push(f));
    }
  }
};
</script>

<style scoped>
.subfleets-container {
  display: block;
  width: 24rem;
  overflow-x: visible;
  overflow-y: auto;
  padding-top: 1rem;
  box-sizing: border-box;
  margin-left: -2rem;
  margin-right: -2rem;
  padding-left: 2rem;
  padding-right: 2rem;
}
.copy-success-overlay {
  position: relative;
  background-color: var(--color-button-normal);
  border-radius: 0.5rem;
  transition: opacity 0.05s ease-in-out;
  opacity: 0;
}
.copy-success-overlay-visible {
  opacity: 1;
}
.copy-success-overlay span {
  display: inline-block;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  font-size: 2rem;
}
</style>
